import {
  Blocker,
  createBrowserHistory,
  createHashHistory,
  createMemoryHistory,
  History,
  Listener,
  Location,
  To,
} from 'history';
import { inject, injectable } from 'inversify';

import { RouterType } from '@/interfaces/settings.interfaces';

import ConfigService from '../ConfigService';
import ServiceTypes from '../ServiceTypes';

import type { INavigationService } from './types';

/**
 * Сервис навигации (History API)
 * Используется библиотекой react-router и для прямого управления историей навигации в браузере
 */
@injectable()
class NavigationService implements INavigationService {
  _type: RouterType;

  _history: History;

  constructor(@inject(ServiceTypes.Config) private _configService: ConfigService) {
    const {
      navigation: { type, initialEntries },
    } = this._configService.settings;
    this._type = type;
    switch (type) {
      case RouterType.Memory:
        this._history = createMemoryHistory({ initialEntries });
        break;
      case RouterType.Browser:
        this._history = createBrowserHistory();
        break;
      case RouterType.Hash:
      default:
        this._history = createHashHistory();
        break;
    }
  }

  get location() {
    if (this._type === RouterType.Browser) {
      return window.location as unknown as Location;
    }
    return this._history.location;
  }

  get action() {
    return this._history.action;
  }

  get history() {
    return this._history;
  }

  go(delta: number) {
    return this._history.go(delta);
  }

  back() {
    return this._history.back();
  }

  forward() {
    return this._history.forward();
  }

  createHref(to: To) {
    return this._history.createHref(to);
  }

  push(to: To, state?: any): void {
    return this._history.push(to, state);
  }

  replace(to: To, state?: any): void {
    return this._history.replace(to, state);
  }

  block(blocker: Blocker) {
    return this._history.block(blocker);
  }

  listen(listener: Listener) {
    return this._history.listen(listener);
  }
}

export default NavigationService;
